export const STRUCTURED_PREFIX = '__PC_STRUCTURED__'
export const DOCUMENT_PREFIX = '__PC_DOCUMENT__'
export const CV_URL_REGEX = /📎\s*CV\s*:\s*(https?:\/\/\S+)/i
export const ATTACHMENT_URL_REGEX = /📎\s*Pièce jointe\s*\(([^)]+)\)\s*:\s*(https?:\/\/\S+)/i

export const DOC_ALLOWED_MIMES = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg']
export const DOC_MAX_BYTES = 10 * 1024 * 1024

export const parseStructuredPayload = (message) => {
  if (message.text?.startsWith(STRUCTURED_PREFIX)) {
    const rawPayload = message.text.slice(STRUCTURED_PREFIX.length)
    try {
      const parsed = JSON.parse(rawPayload)
      if (parsed?.kind === 'contract' && parsed.data?.title) return parsed
      if (parsed?.kind === 'quote' && parsed.data?.title) return parsed
    } catch {
      // fallback below
    }
  }

  if (message.contentType === 'contract' && message.contractData) {
    return { kind: 'contract', data: message.contractData }
  }
  if (message.contentType === 'quote' && message.quoteData) {
    return { kind: 'quote', data: message.quoteData }
  }

  return null
}

export const parseDocumentReference = (messageText) => {
  if (!messageText?.startsWith(DOCUMENT_PREFIX)) return null
  const raw = messageText.slice(DOCUMENT_PREFIX.length)
  const [documentId, fallbackText] = raw.split('::')
  if (!documentId) return null
  return { documentId, fallbackText: fallbackText || 'Document envoyé' }
}

export const buildAttachmentMessageText = (fileName, url) =>
  `📎 Pièce jointe (${fileName}) : ${url}`

export const parseAttachmentMessage = (text) => {
  if (!text) return null
  const match = text.match(ATTACHMENT_URL_REGEX)
  if (!match) return null
  return { fileName: match[1], url: match[2] }
}

export const extractCvUrl = (messages) => {
  for (const m of messages) {
    if (m.type !== 'USER' || !m.text) continue
    const match = m.text.match(CV_URL_REGEX)
    if (match?.[1]) return match[1]
  }
  return null
}

export const extractCvEntry = (messages, candidateName) => {
  for (const m of messages) {
    if (m.type !== 'USER' || !m.text) continue
    const match = m.text.match(CV_URL_REGEX)
    if (match?.[1]) {
      return {
        url: match[1],
        label: candidateName ? `CV de ${candidateName}` : 'CV du candidat',
        date: m.createdAt,
        senderName: m.sender?.name,
      }
    }
  }
  return null
}

export const getDocumentViewUrl = (doc) => {
  if (!doc) return null
  return doc.signedPdfUrl || doc.pdfUrl || doc.fileUrl || null
}

export const inferFileKindFromUrl = (url) => {
  if (!url) return 'unknown'
  const lower = url.split('?')[0].toLowerCase()
  if (/\.(jpg|jpeg|png|gif|webp|bmp)$/.test(lower)) return 'image'
  if (/\.pdf$/.test(lower)) return 'pdf'
  if (lower.includes('image')) return 'image'
  return 'pdf'
}

export const buildDocumentPayloadSummary = (doc) => {
  const payload = doc?.payloadJson
  if (!payload || typeof payload !== 'object') return null

  const rows = []
  if (payload.description) rows.push({ label: 'Description', value: payload.description })
  if (payload.amount != null) rows.push({ label: 'Montant', value: `${payload.amount} €` })
  if (payload.duration) rows.push({ label: 'Durée', value: payload.duration })
  if (payload.total != null) rows.push({ label: 'Total', value: `${payload.total} €` })
  if (payload.validUntil) rows.push({ label: 'Validité', value: payload.validUntil })
  if (payload.dueDate) rows.push({ label: 'Échéance', value: payload.dueDate })
  if (Array.isArray(payload.items) && payload.items.length) {
    payload.items.forEach((item, i) => {
      if (item?.label) {
        rows.push({
          label: item.label,
          value: item.amount != null ? `${item.amount} €` : '—',
        })
      }
    })
  }
  if (payload.originalName) rows.push({ label: 'Fichier', value: payload.originalName })

  return rows.length ? rows : null
}

export const dedupeDocuments = (docs) => {
  const map = new Map()
  for (const doc of docs) {
    if (doc?.id) map.set(doc.id, doc)
  }
  return Array.from(map.values())
}

export const formatMessageTime = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  const now = new Date()
  const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24))
  if (diff === 0) return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  if (diff === 1) return 'Hier'
  if (diff < 7) return date.toLocaleDateString('fr-FR', { weekday: 'short' })
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
}

export const validateDocFile = (file) => {
  if (file.size > DOC_MAX_BYTES) return 'Le fichier ne doit pas dépasser 10 Mo'
  if (!DOC_ALLOWED_MIMES.includes(file.type)) {
    return 'Format non supporté. Utilisez PDF, JPG ou PNG.'
  }
  return null
}

export const getOtherParticipant = (chat, currentUserId) => {
  if (!chat?.participants) return null
  return chat.participants.find(
    (p) => p.userId !== currentUserId && p.roleInChat !== 'ADMIN'
  )
}

export const getMyRoleInChat = (chat, currentUserId) => {
  const me = chat?.participants?.find((p) => p.userId === currentUserId)
  return me?.roleInChat ?? null
}

export const getMessagingContextTheme = (viewMode) => {
  const isPro = viewMode === 'pro'

  return {
    isPro,
    label: isPro ? 'Espace professionnel' : 'Espace particulier',
    shortLabel: isPro ? 'Pro' : 'Particulier',
    badgeClass: isPro
      ? 'bg-amber-50 text-amber-800 border-amber-200'
      : 'bg-slate-100 text-slate-700 border-slate-200',
    bannerClass: isPro
      ? 'bg-amber-50/90 border-amber-200 text-amber-900'
      : 'bg-slate-50 border-slate-200 text-slate-700',
    panelAccentClass: isPro ? 'border-t-amber-400' : 'border-t-slate-400',
    composerClass: isPro
      ? 'bg-amber-50/30 border-t border-amber-100'
      : 'bg-white border-t border-slate-200',
    inputWrapperClass: isPro
      ? 'border-2 border-amber-200 bg-white focus-within:border-amber-400 focus-within:ring-2 focus-within:ring-amber-100'
      : 'border-2 border-slate-200 bg-white focus-within:border-slate-400 focus-within:ring-2 focus-within:ring-slate-100',
    securityTitle: isPro ? 'Canal professionnel sécurisé' : 'Canal personnel sécurisé',
    securityText: isPro
      ? 'Échanges commerciaux protégés · Devis, contrats et documents certifiés Click&Pro.'
      : 'Échanges privés protégés · Vous pouvez joindre des fichiers (PDF, JPG, PNG) à vos messages.',
    messageAreaClass: isPro ? 'bg-amber-50/20' : 'bg-slate-50',
    bubbleMineClass: isPro
      ? 'bg-amber-600 rounded-br-md'
      : 'bg-[hsl(var(--primary))] rounded-br-md',
  }
}
